// ---------------------------------------------------------
// Datei: StoerungenFormular.tsx
// Zweck: Formular für die Phase "Nacharbeiten/Störungen melden"
// - Foto wird zuerst auf HubSpot hochgeladen → URL statt Base64 im Webhook
// - DEBUG: console.log von Payload + Response
// - Verlauf: Titel/Eintrag klickbar (Ticket-Link) + Anzeige HubSpot File-ID
// - Ticket-Link wird primär aus Response-Text extrahiert, z.B.
//   URL:"https://app-eu1.hubspot.com/contacts/144820044/record/0-5/411638524095"
// ---------------------------------------------------------

import React, { useState, useEffect } from 'react';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import FileUploadModal, { UploadFile } from './FileUploadModal';
import api from '../services/api';

const STOERUNGEN_ENDPOINT = '/stoerungen';

interface StoerungenFormularProps {
  dealName: string;
  hubspotId: string;
  onSubmitted?: (submittedAt: string, submittedByEmail: string) => void;
}

interface HistoryEntry {
  id: string;
  timestamp: string;

  /** Text (Beschreibung) */
  text: string;

  /** Ob ein Foto vorhanden war */
  hasImage: boolean;

  projektId: number;
  projektName: string;

  /** Ticket-URL aus Webhook Response */
  ticketUrl?: string;

  /** HubSpot File-ID des hochgeladenen Bildes */
  fotoFileId?: string;

  /** Optional: Die URL, die an Make.com geschickt wurde (signed URL) */
  fotoUrl?: string;
  submittedByEmail?: string;
}

type ProgressStep = 'idle' | 'uploading-image' | 'sending-webhook' | 'waiting-response';

const STEP_LABELS: Record<ProgressStep, string> = {
  idle: '',
  'uploading-image': 'Foto wird auf HubSpot hochgeladen…',
  'sending-webhook': 'Störung wird an Make.com gesendet…',
  'waiting-response': 'Warte auf Ticket-Erstellung…',
};

const STEP_PROGRESS: Record<ProgressStep, number> = {
  idle: 0,
  'uploading-image': 30,
  'sending-webhook': 60,
  'waiting-response': 85,
};

async function uploadImageToHubspot(dataUrl: string, fileName: string): Promise<string> {
  const response = await fetch(dataUrl);
  const blob = await response.blob();

  const formData = new FormData();
  formData.append('file', blob, fileName);

  console.log('[StoerungenFormular] HubSpot Upload Start:', {
    fileName,
    mime: blob.type,
    size: blob.size,
  });

  const result = await api.post<{ fileId: string }>('/hubspot/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  console.log('[StoerungenFormular] HubSpot Upload Response:', result);

  return result.fileId;
}

async function getHubspotFileUrl(fileId: string): Promise<string> {
  console.log('[StoerungenFormular] Get signed url for fileId:', fileId);

  const result = await api.get<{ url: string }>(`/hubspot/file/${fileId}`);

  console.log('[StoerungenFormular] Signed URL Response:', result);

  return result.url;
}


const HISTORY_KEY_PREFIX = 'stoerungen-history-';

function getHistoryKey(hubspotId: string): string {
  return `${HISTORY_KEY_PREFIX}${hubspotId}`;
}

function loadHistory(hubspotId: string): HistoryEntry[] {
  try {
    const raw = localStorage.getItem(getHistoryKey(hubspotId));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveHistory(hubspotId: string, history: HistoryEntry[]) {
  localStorage.setItem(getHistoryKey(hubspotId), JSON.stringify(history));
}

const StoerungenFormular: React.FC<StoerungenFormularProps> = ({ dealName, hubspotId, onSubmitted }) => {
  const { showToast } = useToast();
  const { user } = useAuth();

  const [typ, setTyp] = useState<'nacharbeit' | 'stoerung'>('nacharbeit');
  const [beschreibung, setBeschreibung] = useState('');
  const [selectedImages, setSelectedImages] = useState<UploadFile[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState<HistoryEntry[]>([]);

  const [progressStep, setProgressStep] = useState<ProgressStep>('idle');
  const [lastTicketUrl, setLastTicketUrl] = useState<string | null>(null);

  useEffect(() => {
    setHistory(loadHistory(hubspotId));
  }, [hubspotId]);

  const handleImageSelected = (files: UploadFile[]) => {
    if (files.length > 0) setSelectedImages(prev => [...prev, ...files]);
    setShowUploadModal(false);
  };

  const handleRemoveImage = (id: string) =>
    setSelectedImages(prev => prev.filter(f => f.id !== id));

  const handleSubmit = async () => {
    if (!beschreibung.trim()) {
      showToast('error', 'Bitte eine Beschreibung eingeben', 3000);
      return;
    }

    const projektId = parseInt(hubspotId, 10);
    if (!projektId) {
      showToast('error', 'Ungültige Projekt-ID', 3000);
      return;
    }

    setSending(true);
    setLastTicketUrl(null);

    console.log('[StoerungenFormular] Submit start:', {
      projektId,
      projektName: dealName,
      beschreibungLen: beschreibung.trim().length,
      imageCount: selectedImages.length,
    });

    try {
      // Schritt 1: Alle Bilder hochladen
      const uploadedFotos: { fileId: string; url: string }[] = [];

      if (selectedImages.length > 0) {
        setProgressStep('uploading-image');

        for (let i = 0; i < selectedImages.length; i++) {
          const img = selectedImages[i];
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
          const fileName = `stoerung-${projektId}-${timestamp}-${i + 1}.jpg`;

          console.log(`[StoerungenFormular] Uploading image ${i + 1}/${selectedImages.length}`);
          const fileId = await uploadImageToHubspot(img.dataUrl, fileName);
          const url = await getHubspotFileUrl(fileId);
          uploadedFotos.push({ fileId, url });
        }

        console.log('[StoerungenFormular] All images ready:', uploadedFotos);
      }

      // Schritt 2: Webhook senden
      setProgressStep('sending-webhook');

      const fotoUrls = uploadedFotos.map(f => f.url);
      const fotoFileIds = uploadedFotos.map(f => f.fileId);

      const payload = {
        projektId,
        projektName: dealName,
        typ,
        beschreibung: beschreibung.trim(),
        fotos: fotoUrls,
        fotoFileIds,
        zeitstempel: new Date().toISOString(),
        submittedByEmail: user?.email || 'unbekannt@example.com',
      };

      console.log('[StoerungenFormular] Sending webhook payload:', payload);

      setProgressStep('waiting-response');

      console.log('[StoerungenFormular] Sending payload to backend:', payload);

      const result = await api.post<{ success: boolean; ticketUrl: string | null }>(
        STOERUNGEN_ENDPOINT,
        payload,
      );

      console.log('[StoerungenFormular] Backend response:', result);

      const ticketUrl = result.ticketUrl ?? undefined;

      const newEntry: HistoryEntry = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        timestamp: new Date().toISOString(),
        text: beschreibung.trim(),
        hasImage: uploadedFotos.length > 0,
        projektId,
        projektName: dealName,
        ticketUrl: ticketUrl || undefined,
        fotoFileId: fotoFileIds[0] || undefined,
        fotoUrl: fotoUrls[0] || undefined,
        submittedByEmail: user?.email || 'unbekannt@example.com',
      };

      const updatedHistory = [newEntry, ...history];
      saveHistory(hubspotId, updatedHistory);
      setHistory(updatedHistory);

      setBeschreibung('');
      setSelectedImages([]);

      if (ticketUrl) setLastTicketUrl(ticketUrl);
      onSubmitted?.(newEntry.timestamp, newEntry.submittedByEmail || 'unbekannt@example.com');

      showToast('success', 'Störung erfolgreich gemeldet ✓', 3000);
    } catch (err: unknown) {
      console.error('[StoerungenFormular] Submit error:', err);
      const message = err instanceof Error ? err.message : 'Unbekannter Fehler';
      showToast('error', `Fehler beim Senden: ${message}`, 5000);
    } finally {
      setSending(false);
      setProgressStep('idle');
      console.log('[StoerungenFormular] Submit finished');
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Störung / Nacharbeit melden
        </h2>

        <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-md text-sm text-gray-600 dark:text-gray-300">
          <span className="font-medium">Projekt:</span> {dealName || 'Unbekannt'}
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Typ <span className="text-red-500">*</span>
          </label>
          <select
            className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            value={typ}
            onChange={(e) => setTyp(e.target.value as 'nacharbeit' | 'stoerung')}
            disabled={sending}
          >
            <option value="nacharbeit">Nacharbeit</option>
            <option value="stoerung">Störung</option>
          </select>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Beschreibung <span className="text-red-500">*</span>
          </label>
          <textarea
            className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            rows={4}
            placeholder="Was ist passiert? Beschreibe die Störung oder Nacharbeit..."
            value={beschreibung}
            onChange={(e) => setBeschreibung(e.target.value)}
            disabled={sending}
          />
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Fotos (optional)
          </label>

          {selectedImages.length > 0 && (
            <div className="flex flex-wrap gap-3 mb-3">
              {selectedImages.map((img) => (
                <div key={img.id} className="relative">
                  <img
                    src={img.dataUrl}
                    alt="Vorschau"
                    className="h-24 w-24 object-cover rounded-md border border-gray-200 dark:border-gray-600"
                  />
                  <button
                    onClick={() => handleRemoveImage(img.id)}
                    disabled={sending}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                    title="Foto entfernen"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}

          <button
            onClick={() => setShowUploadModal(true)}
            disabled={sending}
            className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-500 dark:text-gray-400 hover:border-primary-400 hover:text-primary-600 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
              />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {selectedImages.length > 0 ? 'Weiteres Foto hinzufügen' : 'Foto hinzufügen'}
          </button>
        </div>

        {sending && progressStep !== 'idle' && (
          <div className="mb-4">
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">{STEP_LABELS[progressStep]}</p>
            <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2.5 overflow-hidden">
              <div
                className="bg-primary-500 h-2.5 rounded-full transition-all duration-700 ease-in-out"
                style={{ width: `${STEP_PROGRESS[progressStep]}%` }}
              />
            </div>
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={sending || !beschreibung.trim()}
          className={`w-full min-h-[48px] px-6 py-3 rounded-md text-white font-medium flex items-center justify-center gap-2 transition-colors ${
            !sending && beschreibung.trim() ? 'bg-primary-600 hover:bg-primary-700' : 'bg-gray-400 cursor-not-allowed'
          }`}
        >
          {sending ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              Wird gesendet...
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
              Senden
            </>
          )}
        </button>

        {lastTicketUrl && !sending && (
          <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
            <p className="text-sm font-medium text-green-800 dark:text-green-200">Ticket erfolgreich erstellt!</p>
            <a
              href={lastTicketUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-1 text-sm text-primary-600 dark:text-primary-400 hover:underline font-medium"
            >
              Ticket in HubSpot öffnen →
            </a>
          </div>
        )}
      </div>

      {showUploadModal && (
        <FileUploadModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onUpload={handleImageSelected}
          title="Foto hinzufügen"
          allowedTypes={['image/jpeg', 'image/png']}
          multiUpload={true}
        />
      )}

      {/* Verlauf: Titel/Text klickbar wenn ticketUrl vorhanden */}
      {history.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Zuletzt gemeldet</h3>

          <ul className="space-y-3">
            {history.slice(0, 5).map((entry) => {
              const Title = (
                <span className="text-gray-800 dark:text-gray-200 line-clamp-2">{entry.text}</span>
              );

              return (
                <li
                  key={entry.id}
                  className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-md text-sm"
                >
                  <div className="flex-1">
                    {entry.ticketUrl ? (
                      <a
                        href={entry.ticketUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group inline-flex items-start gap-2 hover:underline"
                        title="Ticket öffnen"
                      >
                        <span className="flex-1">{Title}</span>
                        <svg
                          className="w-4 h-4 mt-0.5 opacity-70 group-hover:opacity-100 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          />
                        </svg>
                      </a>
                    ) : (
                      Title
                    )}

                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(entry.timestamp).toLocaleString('de-DE')}
                      {entry.hasImage && ' · 📷 Foto(s)'}
                    </p>
                    {entry.submittedByEmail && (
                      <p className="text-xs text-gray-500 mt-1">
                        Gemeldet von {entry.submittedByEmail}
                      </p>
                    )}

                    {entry.ticketUrl && (
                      <a
                        href={entry.ticketUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mt-2 text-xs text-primary-600 dark:text-primary-400 hover:underline font-medium"
                      >
                        🎫 Ticket öffnen
                      </a>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default StoerungenFormular;
